import { ITag, NeutralColors, mergeStyles } from '@fluentui/react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button } from '@fluentui/react-components';
import { FilterRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import GenericFilterTagItems from './GenericFilterTagItems';
import { IGenericFilterTag } from '../../../utils/types/IGenericFilterTag';
import { MOBILE_NAV_MAX_WIDTH_BREAKPOINT } from '../../../app/common/constants/SiteConstants';
import { iconButtonStyles } from '../../../app/common/styles/CommonStyleObjects';

interface IGenericFilterTagHandlerProps {
  onChange: (filterTags: IGenericFilterTag[]) => void;
  allFilterTags: IGenericFilterTag[];
  children?: JSX.Element;
  hideSavedFilter?: boolean;
}

const filterValueLength = (filterTags: IGenericFilterTag[]) => {
  return filterTags?.map((f) => f.values.length).reduce((a, b) => a + b, 0);
};

const GenericFilterTagHandler = (props: IGenericFilterTagHandlerProps): JSX.Element => {
  const { onChange, allFilterTags, children, hideSavedFilter } = props;
  const [showFilterPanel, setShowFilterPanel] = useState<boolean>(false);

  const filterWrapperClassName = mergeStyles({
    padding: ' 0 5px 0 9px',
    background: filterValueLength(allFilterTags) > 0 ? NeutralColors.gray20 : 'inherit',
    outerWidth: '100%',
    [MOBILE_NAV_MAX_WIDTH_BREAKPOINT]: {
      alignItems: 'flex-start',
      flexFlow: 'column',
    },
  });

  const deleteEmptyArrays = (filterTags: IGenericFilterTag[]) => {
    return filterTags?.filter((f) => f.values.length > 0);
  };

  const getChildFilters = (parentDataKey: string): IGenericFilterTag[] => {
    return allFilterTags?.filter((filter) => filter?.parentDataKey === parentDataKey);
  };

  const getUpdatedFilters = (filterValues: IGenericFilterTag[]): IGenericFilterTag[] => {
    let filters: IGenericFilterTag[] = structuredClone(allFilterTags);

    const updateFilters = (filterValue: IGenericFilterTag) => {
      // Get any associated child filters for this filter
      const childFilters = getChildFilters(filterValue?.dataKey);

      // Recursively update any child filters
      childFilters?.forEach((childFilter) => {
        childFilter.values.length = 0;
        updateFilters(childFilter);
      });

      const indexOfValue = filters?.findIndex((item) => item.dataKey === filterValue.dataKey);
      if (indexOfValue === -1) {
        // Add new filter
        filters = filters.concat([filterValue]);
      } else {
        // Update previous filter
        filters.splice(indexOfValue, 1, {
          dataKey: filters[indexOfValue].dataKey,
          title: filters[indexOfValue].title,
          hideFromBar: filters[indexOfValue].hideFromBar,
          parentDataKey: filters[indexOfValue].parentDataKey,
          values: filterValue.values,
        });
      }
    };

    // For each flter tag update it and append it to the list of tags
    filterValues.forEach((filterValue) => {
      updateFilters(filterValue);
    });
    return filters;
  };

  const onFilterItemUpdate = (filterValue: IGenericFilterTag) => {
    // Get list of updated filters and update state all at once
    const updatedFilters = getUpdatedFilters([filterValue]);
    onChange(updatedFilters);
  };

  const onFilterTagsChange = (filterTagKey: string, filterTagItems: ITag[]): void => {
    const tag: IGenericFilterTag = {
      title: filterTagKey,
      dataKey: filterTagKey,
      values: filterTagItems,
    };
    onFilterItemUpdate(tag);
  };

  const onClearAllFilters = () => {
    let newFilterTags = structuredClone(allFilterTags);
    newFilterTags = newFilterTags.filter((f) => f.dataKey === 'forMeOnly');
    onChange(newFilterTags || []);
  };

  const onFilterPanelUpdate = (newFilterTags: IGenericFilterTag[]) => {
    const cleanedFilterTags = deleteEmptyArrays(newFilterTags);
    onChange(cleanedFilterTags);
    setShowFilterPanel(false);
  };

  const onResetFilters = () => {
    onChange([]);
  };

  return (
    <>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        tokens={{ childrenGap: 20 }}
        className={filterWrapperClassName}
      >
        <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 10 }}>
          {!hideSavedFilter && (
            <>
              <Button appearance="transparent" onClick={onResetFilters}>
                Reset Filter
              </Button>
            </>
          )}
          {allFilterTags?.length > 0 && (
            <GenericFilterTagItems
              filterTagItems={allFilterTags}
              onFilterTagsChange={onFilterTagsChange}
              onClearAllFilters={onClearAllFilters}
            />
          )}
        </Stack>
        <Button
          appearance="subtle"
          icon={<FilterRegular color={iconButtonStyles.color} />}
          title="Edit Filters"
          aria-label="Edit Filters"
          style={{ margin: '4px 0px' }}
          onClick={() => setShowFilterPanel(true)}
        />
      </Stack>
      {showFilterPanel &&
        React.cloneElement(children, {
          currentFilterValues: allFilterTags,
          onFilterUpdate: onFilterPanelUpdate,
          onClosePanel: () => setShowFilterPanel(false),
        })}
    </>
  );
};
export default GenericFilterTagHandler;
